import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Lock, Landmark, UserRound } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import { api } from "@/lib/api";
import PerfilForm from "@/components/perfil/PerfilForm";
import { CuentasManager } from "@/components/entities/CuentasBancarias";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ESTADOS_MX } from "@/config/copiloto";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function EstadoOpera() {
  const { user, refresh } = useAuth();
  const change = async (estado) => {
    try {
      await api.put("/me/estado-opera", { estado });
      await refresh();
      toast.success("Estado actualizado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
  };
  return (
    <section className="bm-card p-5 sm:p-7" data-testid="perfil-estado-opera">
      <h2 className="font-heading font-bold text-slate-900 text-lg">Estado donde opero</h2>
      <p className="text-sm text-slate-600 mt-1 mb-3">El Copiloto te muestra primero el contenido de este estado.</p>
      <Select value={user?.estado_opera || "CDMX"} onValueChange={change}>
        <SelectTrigger className="h-11 w-full sm:w-72" data-testid="perfil-estado-opera-select"><SelectValue /></SelectTrigger>
        <SelectContent>{ESTADOS_MX.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
    </section>
  );
}

function Estado({ perfil }) {
  if (perfil.completo) {
    return (
      <div className="rounded-2xl bg-[#ECFDF5] border border-[#A7F3D0] px-4 py-3 text-sm text-[#065F46] flex items-center gap-2" data-testid="perfil-estado-completo">
        <CheckCircle2 className="w-4 h-4" /> Perfil completo. Tus contratos se llenan solos con estos datos.
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-[#FFFBEB] border border-[#FCD34D] px-4 py-3 text-sm text-[#92400E]" data-testid="perfil-estado-incompleto">
      <div className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Falta completar tu perfil</div>
      <div className="mt-1 text-xs">Falta: {perfil.faltantes.join(", ")}</div>
    </div>
  );
}

const OPCIONES_PRIV = [
  ["bancarios", "No mostrar nunca en pantalla mis datos bancarios"],
  ["curp", "No mostrar nunca en pantalla mi CURP"],
  ["ine", "No mostrar nunca en pantalla mi clave de elector"],
];

function Privacidad({ perfil, onChanged }) {
  const priv = perfil.privacidad || {};
  const toggle = async (k) => {
    const { data } = await api.put("/perfil/privacidad", { ...priv, [k]: !priv[k] });
    onChanged(data);
  };
  return (
    <section className="bm-card p-5 sm:p-7 space-y-3" data-testid="perfil-privacidad">
      <h2 className="font-heading font-bold text-slate-900 text-lg">Privacidad en pantalla</h2>
      <p className="text-sm text-slate-600">Los datos siguen guardados y se usan en tus documentos, pero la app nunca los despliega.</p>
      {OPCIONES_PRIV.map(([k, label]) => (
        <label key={k} className="flex items-center gap-3 text-sm text-slate-800">
          <input type="checkbox" checked={!!priv[k]} onChange={() => toggle(k)} data-testid={`privacidad-${k}`} className="w-4 h-4" />
          {label}
        </label>
      ))}
    </section>
  );
}

export default function MiPerfilPage() {
  const [perfil, setPerfil] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/perfil");
    setPerfil(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="mi-perfil-page">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#1E3A5F] text-white flex items-center justify-center shrink-0"><UserRound className="w-7 h-7" /></div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900">Mi perfil</h1>
            <p className="text-slate-600 mt-1 text-sm sm:text-base">Tus datos como comisionista. Los capturas una vez y viajan a cada contrato.</p>
          </div>
        </div>
        {!perfil ? <div className="text-slate-500">Cargando…</div> : (
          <>
            <Estado perfil={perfil} />
            <section className="bm-card p-5 sm:p-7">
              <h2 className="font-heading font-bold text-slate-900 text-lg mb-4">Datos personales y fiscales</h2>
              <PerfilForm perfil={perfil} onSaved={setPerfil} />
            </section>
            <section className="bm-card p-5 sm:p-7">
              <h2 className="font-heading font-bold text-slate-900 text-lg flex items-center gap-2"><Landmark className="w-5 h-5 text-[#2A5C55]" /> Cuentas para recibir comisiones <span className="text-xs font-semibold text-slate-500">(opcional)</span></h2>
              <p className="text-sm text-slate-600 mt-1 mb-4">La principal se elige sola en cada Comisión Mercantil; puedes cambiarla en el cuestionario.</p>
              <CuentasManager personaId={perfil.persona.id} cuentas={perfil.cuentas} defaultTitular={perfil.persona.nombre} onChanged={load} />
            </section>
            <EstadoOpera />
            <Privacidad perfil={perfil} onChanged={setPerfil} />
            <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="perfil-cifrado-nota">
              <Lock className="w-3.5 h-3.5" /> RFC, CURP, identificaciones y cuentas se guardan cifrados.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
